import React from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { RootStateAnswers } from "../../../questionnaire/store/answers-slice";
import BasicInformationFields from "../common/BasicInformationFields";
import TextInputField from "../common/TextInputField";
import { useProfileForm } from "./ProfileFormContext";
import { useSubject } from "../../providers/SubjectProvider";

/**
 * Profile → Basic information.
 *
 * The contact block from the old Biometrics tab, re-laid as the design's
 * three-column grid, plus the Integration and Client access cards. Save/Cancel
 * are NOT here — they live in the ProfileScreen footer, driven by
 * ProfileFormContext, so one bar serves every tab.
 *
 * The design also shows a "Delete client" card; it is deliberately omitted
 * until a delete-customer endpoint exists (only prescription deletes do today).
 */

const CLIENT_ACCESS_KEYS = [
  { key: "report", label: "Report" },
  { key: "nutrition", label: "Nutrition" },
  { key: "workout", label: "Workout" },
] as const;

const AccessIndicator = ({
  isOn,
  theme,
}: {
  isOn: boolean;
  theme: Theme;
}) => (
  <Box sx={styles.accessIndicator(theme, isOn)}>
    {isOn ? (
      <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
        <path
          d="M3.20703 7.5515C3.83203 8.21391 4.45703 8.08143 4.95703 7.5515L9.95703 2.2522C10.457 1.72227 10.457 0.927377 9.95703 0.397447C9.45703 -0.132482 8.70703 -0.132482 8.20703 0.397447L4.08203 4.76937L2.45703 3.0471C1.95703 2.51717 1.20703 2.51717 0.707031 3.0471C0.207031 3.57703 0.207031 4.37192 0.707031 4.90185L3.20703 7.5515Z"
          fill={theme.colors_ds.SUCCESS_ICON_MAIN}
        />
      </svg>
    ) : (
      <Box sx={styles.accessDash(theme)} />
    )}
  </Box>
);

const BasicInformationTab = () => {
  const theme: Theme = useTheme();
  const clientData = useSelector((state: RootStateAnswers) => state.answers);
  const { isSelf } = useSubject();
  const {
    openPopperId,
    setOpenPopperId,
    inBodyValue,
    onInBodyChange,
    setAddressMissingParts,
  } = useProfileForm();

  return (
    <Box sx={styles.container}>
      {/* Titled like the blocks below it, and like every section on the other
          two tabs — this one was the only headless group on the screen. */}
      <Box sx={styles.block}>
        <Box sx={styles.blockTitle(theme)}>Personal details</Box>
        <BasicInformationFields
          layout="grid"
          openPopperId={openPopperId}
          setOpenPopperId={setOpenPopperId}
          includeBiometricFields
          onAddressCompletenessChange={setAddressMissingParts}
          // Rendered below as its own card instead of a trailing toggles row.
          includeClientAccess={false}
        />
      </Box>

      <Box sx={styles.block}>
        <Box sx={styles.blockTitle(theme)}>Integration</Box>
        <Box sx={styles.card(theme)}>
          <Box sx={styles.cardCopy}>
            <Box sx={styles.cardTitle(theme)}>InBody account</Box>
            <Box sx={styles.cardSubtitle(theme)}>
              {isSelf
                ? "Connects your InBody profile so body-composition scans sync into your biomarkers."
                : "Connects this client's InBody profile so body-composition scans sync into their biomarkers."}
            </Box>
          </Box>
          <Box sx={styles.cardField}>
            <TextInputField
              placeholder="InBody account ID"
              value={inBodyValue}
              name="inBodyAccount"
              handleChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => onInBodyChange(e.target.value)}
            />
          </Box>
        </Box>
      </Box>

      {/* Client access describes what this client may see — an affiliate-facing
          control, so it is hidden on the customer's own Profile. */}
      {!isSelf && (
        <Box sx={styles.block}>
          <Box sx={styles.blockTitle(theme)}>Client access</Box>
          <Box sx={styles.accessRow}>
            {CLIENT_ACCESS_KEYS.map(({ key, label }) => (
              <Box key={key} sx={styles.accessCard(theme)}>
                <Box sx={styles.cardTitle(theme)}>{label}</Box>
                <AccessIndicator
                  isOn={!!clientData.personalInfoInitialAnswers?.[key]}
                  theme={theme}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BasicInformationTab;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "24px",
  },
  block: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
  },
  blockTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  card: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "24px",
    padding: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  cardCopy: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
  },
  cardTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  cardSubtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  cardField: {
    width: "440px",
    flexShrink: 0,
  },
  accessRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "16px",
  },
  accessCard: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "56px",
    padding: "0 16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  accessIndicator: (theme: Theme, isOn: boolean) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    border: `1px solid ${
      isOn
        ? theme.colors_ds.SUCCESS_ICON_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_DEFAULT
    }`,
    backgroundColor: isOn
      ? theme.colorTokens.S[10]
      : theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  accessDash: (theme: Theme) => ({
    width: "10px",
    height: "2px",
    borderRadius: "1px",
    backgroundColor: theme.colors_ds.NEUTRAL_ICON_DEFAULT,
  }),
};
